const MATERIAL_CLASS_PATTERNS = [
  { value: "PVC", pattern: /\b(PVC|AZEK|VERSATEX)\b/i },
  { value: "ACRE", pattern: /\bACRE\b/i },
  { value: "Aluminum", pattern: /\bALUM(?:INUM|INIUM)\b/i },
  { value: "Composite", pattern: /\b(COMPOSITE|HUSK|ECO-HUSK)\b/i },
];

const DEFAULT_MATERIAL_CLASSES = [
  "PVC",
  "ACRE",
  "Aluminum",
  "Composite",
  "Other",
];

// Selectable material classes for the Materials admin form. Mirrors the
// classes the resolver understands so a curated material lands in the same
// class bucket the estimator picks from.
export const MATERIAL_CLASS_OPTIONS = DEFAULT_MATERIAL_CLASSES.map((value) => ({
  value,
  label: value,
}));

// A price older than this reads as potentially stale (amber). Andy's
// materials-hardening threshold — shared by the admin table and the estimator
// "Priced with:" transparency chip so both agree on what "stale" means.
export const STALE_PRICE_THRESHOLD_DAYS = 180;

// Fraction labels for the derived-name suggestion. Straight-quote form ("1/2")
// to match how existing materials are named (e.g. 'PVC 3/4"'), distinct from
// formatters.formatThicknessInches which renders the ″ prime for display.
const THICKNESS_FRACTION_LABELS = {
  0.125: "1/8",
  0.25: "1/4",
  0.375: "3/8",
  0.5: "1/2",
  0.625: "5/8",
  0.75: "3/4",
  0.875: "7/8",
};
const DECORATIVE_SHEET_FORMULA_TYPES = new Set([
  "Bracket",
  "Corbel",
  "RafterTail",
]);
const DECORATIVE_SHEET_NAME_KEYWORDS = ["bracket", "corbel", "rafter", "tail"];
const DECORATIVE_SHEET_CLASSES = new Set(["PVC", "ACRE", "Composite"]);

export const FINISH_READINESS_OPTIONS = [
  {
    value: "ship_ready_for_paint",
    label: "Shipped Ready for Paint",
    helper:
      "Leaves Island finishing out of scope. Use this when the site or painter handles the topcoat.",
  },
  {
    value: "paint_ready",
    label: "Paint Ready",
    helper:
      "Keeps the part prepped for finishing without assuming Island applies the final finish coat.",
  },
  {
    value: "island_prefinished",
    label: "Pre-finished by Island Millwork",
    helper:
      "Keeps in-house finish work in scope so the quote reflects Island-applied finishing.",
  },
];

const FINISH_READINESS_MAP = new Map(
  FINISH_READINESS_OPTIONS.map((option) => [option.value, option]),
);

/**
 * Finish choices for TYPED (rate-card) lines. Andy collapsed the legacy
 * three-option `finish_readiness` list to exactly two costed outcomes
 * (ANSWERED 2026-07-08): Island applies the finish, or the finish is left to
 * others. Stored in the `finish_mode` field so catalog lines keep using
 * `finish_readiness` untouched.
 */
export const FINISH_MODE_OPTIONS = [
  {
    value: "island_finish",
    label: "Island Armor Finish",
    helper:
      "Island applies the finish — paint labor and finish material are in the quote.",
  },
  {
    value: "prep_only",
    label: "Prep for paint — finish by others",
    helper:
      "Site or painter handles the topcoat — paint labor and finish material are excluded.",
  },
];

const FINISH_MODE_MAP = new Map(
  FINISH_MODE_OPTIONS.map((option) => [option.value, option]),
);

/**
 * True when a product prices through the rate-card engine (rate_card_v1). For
 * these products the server resolves the concrete material from the picked
 * material class + the card's default thickness, so the estimator only chooses
 * a class (and may optionally pin a specific sheet via `material_id`).
 */
export function isRateCardProduct(product) {
  const config = product?.calculation_config;
  if (!config || typeof config !== "object") return false;
  return (
    config.engine_version === "rate_card_v1" ||
    Boolean(config.rate_card && typeof config.rate_card === "object")
  );
}

/**
 * True when a line item prices by product type (rate-card engine) rather than a
 * catalog product. Typed lines carry `product_type_key` and no `product_id`, so
 * they behave like rate-card products for material resolution and the
 * "Priced with:" transparency line even though no Product row is involved.
 */
export function isTypedLine(lineItem) {
  return Boolean(lineItem?.product_type_key);
}

export function inferMaterialClassification(materialName) {
  const name = String(materialName || "").trim();
  if (!name) return "";

  const matched = MATERIAL_CLASS_PATTERNS.find(({ pattern }) =>
    pattern.test(name),
  );
  return matched ? matched.value : "Other";
}

export function getFinishReadinessOption(value) {
  return FINISH_READINESS_MAP.get(value) || null;
}

export function getFinishReadinessLabel(value) {
  return getFinishReadinessOption(value)?.label || "";
}

export function getFinishReadinessHelper(value) {
  return getFinishReadinessOption(value)?.helper || "";
}

export function getFinishModeOption(value) {
  return FINISH_MODE_MAP.get(value) || null;
}

export function getFinishModeHelper(value) {
  return getFinishModeOption(value)?.helper || "";
}

export function getCatalogMaterialById(materials = [], materialId) {
  return (
    (materials || []).find(
      (material) =>
        String(material.id) === String(materialId) ||
        Number(material.id) === Number(materialId),
    ) || null
  );
}

function normalizeMaterialOptionKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getProductMaterialRecord(product, materials = []) {
  const embedded = product?.material || null;
  if (!embedded) return null;
  return getCatalogMaterialById(materials, embedded.id) || embedded;
}

export function getProductMaterialOptions(product) {
  const materialOptions = product?.calculation_config?.material_options;
  if (
    !materialOptions ||
    typeof materialOptions !== "object" ||
    !Array.isArray(materialOptions.options) ||
    materialOptions.options.length === 0
  ) {
    return null;
  }

  return {
    default_option_key: normalizeMaterialOptionKey(
      materialOptions.default_option_key,
    ),
    options: materialOptions.options.map((option) => ({
      key: normalizeMaterialOptionKey(option?.key || option?.label),
      label: String(option?.label || "").trim(),
      default_material_id:
        option?.default_material_id !== null &&
        option?.default_material_id !== undefined &&
        option?.default_material_id !== ""
          ? option.default_material_id
          : null,
    })),
  };
}

export function hasProductMaterialOptions(product) {
  return Boolean(getProductMaterialOptions(product)?.options?.length);
}

export function getProductMaterialOptionByKey(product, optionKey) {
  const normalizedKey = normalizeMaterialOptionKey(optionKey);
  if (!normalizedKey) return null;
  return (
    getProductMaterialOptions(product)?.options?.find(
      (option) => option.key === normalizedKey,
    ) || null
  );
}

export function getDefaultProductMaterialOption(product) {
  const materialOptions = getProductMaterialOptions(product);
  if (!materialOptions) return null;

  const explicitDefault = getProductMaterialOptionByKey(
    product,
    materialOptions.default_option_key,
  );
  if (explicitDefault) {
    return explicitDefault;
  }

  return materialOptions.options[0] || null;
}

export function getConfiguredProductMaterialForOption({
  product,
  optionKey,
  materials = [],
}) {
  const option = optionKey
    ? getProductMaterialOptionByKey(product, optionKey)
    : getDefaultProductMaterialOption(product);
  if (!option) return null;

  const catalogMaterial = getCatalogMaterialById(
    materials,
    option.default_material_id,
  );
  if (catalogMaterial) {
    return catalogMaterial;
  }

  if (
    product?.material &&
    String(product.material.id) === String(option.default_material_id)
  ) {
    return product.material;
  }

  return null;
}

export function isArchivedMaterialOptionSelection({ lineItem, product }) {
  if (!hasProductMaterialOptions(product)) {
    return false;
  }

  const optionKey = normalizeMaterialOptionKey(lineItem?.material_option_key);
  if (!optionKey) {
    return false;
  }

  return !getProductMaterialOptionByKey(product, optionKey);
}

export function getLineItemMaterialOptionLabel({ lineItem, product }) {
  const currentOption = getProductMaterialOptionByKey(
    product,
    lineItem?.material_option_key,
  );
  if (currentOption?.label) {
    return currentOption.label;
  }

  const savedLabel = String(lineItem?.material_option_label || "").trim();
  if (savedLabel) {
    return savedLabel;
  }

  return getDefaultProductMaterialOption(product)?.label || "";
}

function getProductFormulaType(product) {
  return product?.calculation_rules?.formula_type || "";
}

function isDecorativeSheetProduct(product) {
  const formulaType = getProductFormulaType(product);
  if (DECORATIVE_SHEET_FORMULA_TYPES.has(formulaType)) {
    return true;
  }

  const nameLower = String(product?.name || "").toLowerCase();
  return DECORATIVE_SHEET_NAME_KEYWORDS.some((keyword) =>
    nameLower.includes(keyword),
  );
}

function toNumericDimension(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isRoughlyEqual(value, target) {
  return value != null && Math.abs(value - target) < 0.01;
}

function isHalfInchSheetName(materialName) {
  const nameLower = String(materialName || "").toLowerCase();
  return /(?:^|[^0-9])(1\/2|0?\.5)(?:\s*"|\s*in(?:ch(?:es)?)?)?(?!\d)/.test(
    nameLower,
  );
}

function scoreDecorativeSheetMaterial(material, classification) {
  if (!material) return -1;

  const materialClassification = inferMaterialClassification(material?.name);
  if (classification && materialClassification !== classification) {
    return -1;
  }

  const nameLower = String(material?.name || "").toLowerCase();
  const sheetWidth = toNumericDimension(material?.sheet_width_in);
  const sheetLength = toNumericDimension(material?.sheet_length_in);
  const hasSheetDims = sheetWidth != null && sheetLength != null;
  const isFourByTwelveSheet =
    (isRoughlyEqual(sheetWidth, 48) && isRoughlyEqual(sheetLength, 144)) ||
    (isRoughlyEqual(sheetWidth, 144) && isRoughlyEqual(sheetLength, 48));

  let score = 0;
  if (nameLower.includes("sheet")) score += 8;
  if (hasSheetDims) score += 4;
  if (material?.cost_per_sheet != null) score += 2;
  if (isFourByTwelveSheet) score += 16;
  if (isHalfInchSheetName(material?.name)) score += 10;
  return score;
}

function findPreferredDecorativeSheet({ classification, materials = [] }) {
  if (!classification || !DECORATIVE_SHEET_CLASSES.has(classification)) {
    return null;
  }

  let bestMaterial = null;
  let bestScore = 0;

  for (const material of materials || []) {
    const score = scoreDecorativeSheetMaterial(material, classification);
    if (score > bestScore) {
      bestMaterial = material;
      bestScore = score;
    }
  }

  return bestMaterial;
}

function findMaterialByClassification(classification, materials = []) {
  if (!classification) return null;
  return (
    (materials || []).find(
      (material) =>
        inferMaterialClassification(material?.name) === classification,
    ) || null
  );
}

function resolveProductDefaultMaterial({
  classification,
  product,
  materials = [],
} = {}) {
  if (hasProductMaterialOptions(product)) {
    const configuredMaterial = getConfiguredProductMaterialForOption({
      product,
      materials,
    });
    if (configuredMaterial) {
      return configuredMaterial;
    }
  }

  const productMaterial = getProductMaterialRecord(product, materials);
  const effectiveClassification =
    classification || inferMaterialClassification(productMaterial?.name) || "";

  if (isDecorativeSheetProduct(product)) {
    const preferredSheet = findPreferredDecorativeSheet({
      classification: effectiveClassification,
      materials,
    });
    if (preferredSheet) {
      return preferredSheet;
    }
  }

  if (
    productMaterial &&
    (!effectiveClassification ||
      inferMaterialClassification(productMaterial?.name) ===
        effectiveClassification)
  ) {
    return productMaterial;
  }

  return findMaterialByClassification(effectiveClassification, materials);
}

export function buildMaterialClassificationOptions(
  materials = [],
  products = [],
) {
  const seen = new Set();

  const capture = (materialName) => {
    const next = inferMaterialClassification(materialName);
    if (next) seen.add(next);
  };

  (materials || []).forEach((material) => capture(material?.name));
  (products || []).forEach((product) => capture(product?.material?.name));

  const orderedValues = [
    ...DEFAULT_MATERIAL_CLASSES.filter((value) => seen.has(value)),
    ...DEFAULT_MATERIAL_CLASSES.filter((value) => !seen.has(value)),
  ];

  return orderedValues.map((value) => ({ value, label: value }));
}

export function resolveMaterialSelection({
  classification,
  lineItem,
  product,
  materials = [],
}) {
  const effectiveClassification =
    classification ||
    inferMaterialClassification(
      getCatalogMaterialById(materials, lineItem?.material_id)?.name,
    ) ||
    inferMaterialClassification(
      getProductMaterialRecord(product, materials)?.name,
    ) ||
    "";

  if (!effectiveClassification) {
    return resolveProductDefaultMaterial({
      classification,
      product,
      materials,
    });
  }

  const currentMaterial = getCatalogMaterialById(
    materials,
    lineItem?.material_id,
  );
  if (
    currentMaterial &&
    inferMaterialClassification(currentMaterial.name) ===
      effectiveClassification
  ) {
    if (!isDecorativeSheetProduct(product)) {
      return currentMaterial;
    }

    const preferredSheet = findPreferredDecorativeSheet({
      classification: effectiveClassification,
      materials,
    });
    return preferredSheet || currentMaterial;
  }

  return resolveProductDefaultMaterial({
    classification: effectiveClassification,
    product,
    materials,
  });
}

export function getResolvedLineItemMaterial({
  lineItem,
  product,
  materials = [],
}) {
  if (hasProductMaterialOptions(product)) {
    const currentMaterial = getCatalogMaterialById(
      materials,
      lineItem?.material_id,
    );
    if (lineItem?.material_selection_mode === "line_item_override") {
      return currentMaterial;
    }

    const hasSavedOption = Boolean(
      lineItem?.material_option_key || lineItem?.material_option_label,
    );
    if (hasSavedOption) {
      return (
        currentMaterial ||
        getConfiguredProductMaterialForOption({
          product,
          optionKey: lineItem?.material_option_key,
          materials,
        })
      );
    }

    return (
      getConfiguredProductMaterialForOption({
        product,
        materials,
      }) ||
      currentMaterial ||
      getProductMaterialRecord(product, materials)
    );
  }

  const productMaterial = getProductMaterialRecord(product, materials);
  const currentMaterial = getCatalogMaterialById(
    materials,
    lineItem?.material_id,
  );
  const effectiveClassification =
    lineItem?.material_classification ||
    inferMaterialClassification(currentMaterial?.name) ||
    inferMaterialClassification(productMaterial?.name) ||
    "";

  if (lineItem?.material_selection_mode === "line_item_override") {
    return resolveMaterialSelection({
      classification: effectiveClassification,
      lineItem,
      product,
      materials,
    });
  }

  return resolveProductDefaultMaterial({
    classification: effectiveClassification,
    product,
    materials,
  });
}

/**
 * Whole days between a material's `price_updated_at` and now. Returns null for
 * missing/blank/unparseable timestamps; clamps future timestamps to 0.
 * `now` accepts a Date or epoch ms so callers (and tests) can pin the clock.
 */
export function getPriceAgeDays(priceUpdatedAt, now = Date.now()) {
  if (
    priceUpdatedAt === null ||
    priceUpdatedAt === undefined ||
    priceUpdatedAt === ""
  ) {
    return null;
  }
  const then = new Date(priceUpdatedAt).getTime();
  if (Number.isNaN(then)) return null;
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  if (Number.isNaN(nowMs)) return null;
  const diffMs = nowMs - then;
  if (diffMs <= 0) return 0;
  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

/** True when a material's price is older than STALE_PRICE_THRESHOLD_DAYS. */
export function isPriceStale(priceUpdatedAt, now = Date.now()) {
  const days = getPriceAgeDays(priceUpdatedAt, now);
  return days != null && days > STALE_PRICE_THRESHOLD_DAYS;
}

/**
 * Relative-age label for a price timestamp: "today", "1 day ago", "3 days ago",
 * "2 months ago", "14 months ago", "2 years ago". Returns "" for missing/
 * unparseable timestamps. Months past a year are kept as months (matching the
 * "14 months ago" staleness cue) until they roll into whole years at 24+.
 */
export function formatPriceAge(priceUpdatedAt, now = Date.now()) {
  const days = getPriceAgeDays(priceUpdatedAt, now);
  if (days == null) return "";
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 24) return months === 1 ? "1 month ago" : `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

function formatThicknessForName(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "";
  const whole = Math.trunc(num);
  const frac = Math.round((num - whole) * 1000) / 1000;
  const fracLabel = THICKNESS_FRACTION_LABELS[frac];
  if (fracLabel) {
    return whole > 0 ? `${whole}-${fracLabel}"` : `${fracLabel}"`;
  }
  return `${Number(num.toFixed(3))}"`;
}

/**
 * Derived name suggestion from a material class + thickness, e.g.
 * ("PVC", 0.5) → 'PVC 1/2"'. Returns "" without a class. Never applied
 * automatically — the admin form offers it as a one-click affordance so a
 * curator's custom naming is never clobbered.
 */
export function buildSuggestedMaterialName(materialClass, thicknessIn) {
  const klass = String(materialClass || "").trim();
  if (!klass) return "";
  const thickness = formatThicknessForName(thicknessIn);
  return thickness ? `${klass} ${thickness}` : klass;
}
